import type { RenderNode, RenderTree } from '@inf-monkeys-tech/monkeys';
import type { ReactNode } from 'react';

import { RenderTreeRuntimeHost } from './RenderTreeRuntimeHost';
import type { RenderTreeRuntimeHostProps } from './RenderTreeRuntimeHost';
import type { RenderTreeNodeContext } from './RenderTreeHost';

export interface RenderTreeRegistryNodeContext<TBinding = unknown> extends RenderTreeNodeContext {
  binding: TBinding | undefined;
  model: RenderNode['renderModel'];
}

export type RenderTreeComponentRenderer<TBinding = unknown> = (
  context: RenderTreeRegistryNodeContext<TBinding>,
) => ReactNode;

export type RenderTreeComponentRegistry = Readonly<
  Record<string, RenderTreeComponentRenderer>
>;

export type RenderTreeBindingRegistry = Readonly<Record<string, unknown>>;

export interface RenderTreeRegistryHostProps
  extends Omit<RenderTreeRuntimeHostProps, 'renderNode' | 'tree'> {
  tree: RenderTree;
  components: RenderTreeComponentRegistry;
  bindings?: RenderTreeBindingRegistry;
  resolveBinding?: (
    node: RenderNode,
    bindings: RenderTreeBindingRegistry,
  ) => unknown;
  renderMissingComponent?: (context: RenderTreeNodeContext) => ReactNode;
}

const defaultResolveBinding = (
  node: RenderNode,
  bindings: RenderTreeBindingRegistry,
) => {
  const bindingId = node.dataContextRef?.id;
  return bindingId ? bindings[bindingId] : undefined;
};

export function RenderTreeRegistryHost({
  tree,
  components,
  bindings = {},
  resolveBinding = defaultResolveBinding,
  renderMissingComponent,
  ...props
}: RenderTreeRegistryHostProps) {
  return (
    <RenderTreeRuntimeHost
      {...props}
      tree={tree}
      renderNode={(context) => {
        const renderer = components[context.node.capabilityRef.id];
        if (!renderer) {
          return renderMissingComponent?.(context) ?? context.children;
        }
        return renderer({
          ...context,
          binding: resolveBinding(context.node, bindings),
          model: context.node.renderModel,
        });
      }}
    />
  );
}
